import { GuidT, SelectIF } from '@/types/common';
import {
    WARDROBE_CATEGORY_TABS,
    WardrobeCategoryIF,
    WardrobeCategoryT,
    WardrobeCategoryTabConfigIF,
} from '@/constants/wardrobeCategoryTabs';

export const getWardrobeParentCategories = (
    categories: SelectIF<GuidT>[] | null | undefined,
): SelectIF<GuidT>[] => {
    if (!categories?.length) {
        return [];
    }

    return categories.filter((category) => category.settings?.parentId == null);
};

const createServiceWardrobeCategory = (
    config: WardrobeCategoryTabConfigIF,
): WardrobeCategoryIF => ({
    id: config.type,
    type: config.type,
    group: 'service',
    label: config.label ?? '',
    icon: config.icon,
});

const createClothingWardrobeCategory = (
    config: WardrobeCategoryTabConfigIF,
    parentCategories: SelectIF<GuidT>[],
): WardrobeCategoryIF | null => {
    if (!config.serverLabel) {
        return null;
    }

    const serverCategory = parentCategories.find(
        (category) => category.label === config.serverLabel,
    );

    if (!serverCategory) {
        return null;
    }

    return {
        id: serverCategory.value,
        type: config.type,
        group: 'clothing',
        label: serverCategory.label,
        icon: config.icon,
        categoryGuid: serverCategory.value,
    };
};

export const createWardrobeCategories = (
    categories: SelectIF<GuidT>[] | null | undefined,
): WardrobeCategoryIF[] => {
    const parentCategories = getWardrobeParentCategories(categories);
    const result: WardrobeCategoryIF[] = [];

    WARDROBE_CATEGORY_TABS.forEach((config) => {
        if (config.group === 'service') {
            result.push(createServiceWardrobeCategory(config));
            return;
        }

        const clothingCategory = createClothingWardrobeCategory(
            config,
            parentCategories,
        );

        if (clothingCategory) {
            result.push(clothingCategory);
        }
    });

    return result;
};

export const getWardrobeParentCategoryGuids = (
    categories: SelectIF<GuidT>[] | null | undefined,
): Set<GuidT> =>
    new Set(
        getWardrobeParentCategories(categories).map(
            (category) => category.value,
        ),
    );

export const getWardrobeCategoryByType = (
    categories: WardrobeCategoryIF[],
    categoryType: WardrobeCategoryT | undefined,
): WardrobeCategoryIF | undefined => {
    if (!categoryType) {
        return undefined;
    }

    return categories.find((category) => category.type === categoryType);
};

export const getWardrobeCategoryTypeByGuid = (
    categories: WardrobeCategoryIF[],
    categoryGuid?: GuidT,
): WardrobeCategoryT | undefined => {
    if (!categories || !categoryGuid) {
        return undefined;
    }

    return categories.find((category) => category.categoryGuid === categoryGuid)
        ?.type;
};

export const getWardrobeCategoryTabByGuids = (
    categoryGuids: string[] | null,
    categoriesProperties: SelectIF<GuidT>[],
    wardrobeCategories: WardrobeCategoryIF[],
): WardrobeCategoryT | null => {
    if (!categoryGuids?.length) {
        return null;
    }

    const parentGuids = new Set<string>();

    categoryGuids.forEach((guid) => {
        const parentGuid =
            categoriesProperties.find((category) => category.value === guid)
                ?.settings?.parentId ?? guid;

        parentGuids.add(parentGuid);
    });

    if (parentGuids.size !== 1) {
        return null;
    }

    const [parentGuid] = Array.from(parentGuids);

    if (!parentGuid) {
        return null;
    }

    const matchedCategory = wardrobeCategories.find(
        (category) => category.categoryGuid === parentGuid,
    );

    return matchedCategory?.type ?? null;
};
